import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import ProductionJobForm from '@/components/manufacturing/ProductionJobForm';
import ManufacturingService from '@/api/manufacturing.service';

const EditProductionJob: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [productionJob, setProductionJob] = useState<any>(null);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchProductionJob();
    }
  }, [id]);

  const fetchProductionJob = async () => {
    try {
      setIsDataLoading(true);
      const response = await ManufacturingService.getProductionJob(businessId, parseInt(id!));
      
      if (response.data?.success) {
        setProductionJob(response.data.data);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load production job details",
        });
        navigate('/manufacturing/production-jobs');
      }
    } catch (error: any) {
      console.error('Error fetching production job:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load production job details",
      });
      navigate('/manufacturing/production-jobs');
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      const response = await ManufacturingService.updateProductionJob(businessId, parseInt(id!), data);

      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Production job updated successfully",
        });
        navigate('/manufacturing/production-jobs');
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to update production job",
        });
      }
    } catch (error: any) {
      console.error('Error updating production job:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update production job",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isDataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading production job details...</span>
        </div>
      </div>
    );
  }

  if (!productionJob) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Production Job Not Found</h3>
          <p className="text-muted-foreground">The requested production job could not be found.</p>
          <Button 
            onClick={() => navigate('/manufacturing/production-jobs')}
            className="mt-4"
          >
            Back to Production Jobs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/manufacturing/production-jobs')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Production Jobs
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Production Job</h1>
        <p className="text-muted-foreground">
          Update production job details - {productionJob.jobNumber}
        </p>
      </div>

      <ProductionJobForm 
        initialData={productionJob}
        onSubmit={handleSubmit} 
        isLoading={isLoading} 
      />
    </div>
  );
};

export default EditProductionJob; 